// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { sGetRaw } from "../lib/scopedStorage";
import type { SupabaseStorageProvider } from "../lib/supabaseStorage";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

// ── Context builder ───────────────────────────────────────────────────────────

function walkItemIds(rows: any[]): string[] {
  const ids: string[] = [];
  for (const r of rows || []) {
    if (r.type === "item" || (!r.type && r.id && !r.subcategories && !r.rows && !r.children)) ids.push(r.id);
    if (r.rows) ids.push(...walkItemIds(r.rows));
    if (r.children) ids.push(...walkItemIds(r.children));
  }
  return ids;
}

function shortDivName(name: string): string {
  return (name || "")
    .replace(/^Division \d+ [–-] /, "")
    .replace(/^Div \d+ [–-] /, "")
    .replace(/^0?\d+ [–-] /, "");
}

function buildContextDocument(loadedStates: Array<{ id: string; name: string; state: any }>): string {
  if (!loadedStates.length) return "No active projects found.";

  const parts: string[] = [
    `OPENESTIMATE PORTFOLIO — ${loadedStates.length} active project${loadedStates.length !== 1 ? "s" : ""}`,
    "",
  ];

  for (const { id, name, state } of loadedStates) {
    const s = state || {};
    const projectName  = s.projectName || name;
    const address      = s.address || "";
    const buildingArea = Number(s.buildingArea) || 0;
    const duration     = Number(s.duration) || 0;
    const overhead     = Number(s.overhead) || 0;
    const profit       = Number(s.profit) || 0;
    const contingency  = Number(s.contingency) || 0;
    const itemState    = s.itemState || {};
    const divisions    = s.divisions || [];
    const proposals    = s.proposals || [];
    const bidPartners  = s.bidPartners || [];
    const bids         = s.bids || [];
    const bidScopes    = s.bidScopes || [];
    const bidAlignments = s.bidAlignments || {};
    const phases       = s.schedulePlanPhases || s.phases || [];

    // ── Compute totals ──
    let subtotal = 0;
    const divTotals: Record<string, { name: string; total: number }> = {};
    for (const div of divisions) {
      let divTotal = 0;
      for (const sub of div.subcategories || []) {
        for (const itemId of walkItemIds(sub.rows || [])) {
          const item = itemState[itemId];
          if (!item || !item.included) continue;
          if (item.altType && !(item.altType === "Alt" && item.altStatus === "Accepted")) continue;
          divTotal += (item.qty || 0) * (item.unitCost || 0);
        }
      }
      if (divTotal > 0) {
        divTotals[div.id] = { name: shortDivName(div.name || div.id), total: divTotal };
        subtotal += divTotal;
      }
    }
    const grandTotal = subtotal * (1 + overhead / 100) * (1 + profit / 100) * (1 + contingency / 100);
    const costPerSF = buildingArea > 0 ? grandTotal / buildingArea : 0;

    const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
    const fmtSF = (n: number) => buildingArea > 0 ? ` ($${(n / buildingArea).toFixed(0)}/SF)` : "";

    parts.push("─".repeat(60));
    parts.push(`PROJECT: ${projectName}${address ? `  |  ${address}` : ""}`);
    parts.push(`Total: ${fmt(grandTotal)}${costPerSF > 0 ? `  |  $${costPerSF.toFixed(0)}/SF` : ""}${buildingArea > 0 ? `  |  ${buildingArea.toLocaleString()} SF` : ""}`);
    parts.push(`Subtotal: ${fmt(subtotal)}  |  Overhead: ${overhead}%  |  Profit: ${profit}%  |  Contingency: ${contingency}%`);
    if (duration > 0) parts.push(`Duration: ${duration} months`);
    if (proposals.length > 1) parts.push(`Packages: ${proposals.map((p: any) => p.name).join(", ")}`);

    // Division totals
    if (Object.keys(divTotals).length > 0) {
      parts.push("Division totals:");
      for (const [, div] of Object.entries(divTotals)) {
        parts.push(`  ${div.name}: ${fmt(div.total)}${fmtSF(div.total)}`);
      }
    }

    // ── Line items ──
    const itemLines: string[] = [];
    for (const div of divisions) {
      const divShort = shortDivName(div.name || div.id);
      for (const sub of div.subcategories || []) {
        for (const itemId of walkItemIds(sub.rows || [])) {
          const item = itemState[itemId];
          if (!item || !item.included) continue;
          if (item.altType && !(item.altType === "Alt" && item.altStatus === "Accepted")) continue;
          const qty = item.qty || 0;
          const unit = item.unit || "LS";
          const unitCost = item.unitCost || 0;
          const total = qty * unitCost;
          if (total <= 0) continue;

          const desc = item.description || "—";
          let line = `  [${divShort}] ${desc}: ${qty} ${unit} @ $${unitCost.toLocaleString()}/${unit} = ${fmt(total)}`;

          // Trade attribution
          const tradeName = item.tradeName || null;
          const srcBidId  = item.sourceBidId || null;
          if (tradeName) {
            line += `  (carrier: ${tradeName})`;
          } else if (srcBidId) {
            const srcBid = (bids as any[]).find((b: any) => b.id === srcBidId);
            if (srcBid) {
              const partner = (bidPartners as any[]).find((p: any) => p.id === srcBid.partnerId);
              if (partner) line += `  (carrier: ${partner.name})`;
            }
          }

          // Cost status
          if (item.costStatus && item.costStatus !== "Hard Bid") line += `  [${item.costStatus}]`;

          // Proposal package
          if (proposals.length > 1) {
            const prop = proposals.find((p: any) => p.id === item.proposal);
            if (prop) line += `  {${prop.name}}`;
          }

          itemLines.push(line);
        }
      }
    }
    if (itemLines.length > 0) {
      parts.push(`Line items (${itemLines.length} included):`);
      // Cap at 300 items to keep context reasonable; most significant first (by total)
      const sorted = itemLines.slice().sort((a, b) => {
        const getTotal = (l: string) => {
          const m = l.match(/= \$([0-9,]+)/);
          return m ? parseInt(m[1].replace(/,/g, ""), 10) : 0;
        };
        return getTotal(b) - getTotal(a);
      });
      parts.push(...sorted.slice(0, 300));
      if (sorted.length > 300) parts.push(`  ... and ${sorted.length - 300} more smaller items`);
    }

    // ── Bid trade partners & scopes ──
    if (bidPartners.length > 0) {
      parts.push("Trade partners (bid leveling):");
      for (const partner of bidPartners as any[]) {
        const partnerBids = (bids as any[]).filter((b: any) => b.partnerId === partner.id);
        const scopeNames = partnerBids.flatMap((b: any) => {
          const scope = (bidScopes as any[]).find((sc: any) => sc.id === b.scopeId);
          return scope ? [scope.name] : [];
        });
        const uniqueScopes = [...new Set(scopeNames)];
        parts.push(`  ${partner.name}${uniqueScopes.length > 0 ? ` → scopes: ${uniqueScopes.join(", ")}` : ""}`);
      }
    }

    // ── Schedule phases ──
    if (phases.length > 0) {
      parts.push("Schedule phases:");
      for (const ph of phases as any[]) {
        const start = ph.startDate || ph.start || "";
        const end   = ph.endDate || ph.end || "";
        const dur   = ph.duration || ph.durationWeeks || ph.durationMonths || 0;
        const durLabel = ph.durationWeeks ? `${ph.durationWeeks}wk` : ph.durationMonths ? `${ph.durationMonths}mo` : dur ? `${dur}` : "";
        const budget = ph.budget || ph.cost || 0;
        let phLine = `  ${ph.name || ph.label || "Phase"}`;
        if (durLabel) phLine += `: ${durLabel}`;
        if (start && end) phLine += `  (${start} → ${end})`;
        else if (start) phLine += `  (starts ${start})`;
        if (budget > 0) phLine += `  ${fmt(budget)}`;
        parts.push(phLine);
      }
    }

    parts.push("");
  }

  return parts.join("\n");
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface Message {
  role: "user" | "assistant";
  content: string;
  error?: boolean;
}

interface Props {
  storage: SupabaseStorageProvider;
  projects: Array<{ id: string; name: string; isActive: boolean }>;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ProjectAssistant({ storage, projects, onClose }: Props) {
  const [isDarkMode] = useState<boolean>(() => {
    try { const s = sGetRaw("oe_dark_mode"); return s === null ? true : s === "true"; } catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [messages, setMessages]               = useState<Message[]>([]);
  const [input, setInput]                     = useState("");
  const [loading, setLoading]                 = useState(false);
  const [contextReady, setContextReady]       = useState(false);
  const [loadingContext, setLoadingContext]    = useState(false);
  const [contextDoc, setContextDoc]           = useState<string | null>(null);
  const [convHistory, setConvHistory]         = useState<Array<{ role: string; content: string }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Load all active project states lazily on first question
  const ensureContext = useCallback(async (): Promise<string> => {
    if (contextDoc) return contextDoc;
    setLoadingContext(true);
    try {
      const activeProjects = projects.filter(p => p.isActive !== false);
      const loaded: Array<{ id: string; name: string; state: any }> = [];
      for (const p of activeProjects) {
        try {
          const state = await (storage as any).loadProjectById(p.id);
          if (state) loaded.push({ id: p.id, name: p.name, state });
        } catch (err) {
          console.warn("ProjectAssistant: failed to load project", p.id, err);
        }
      }
      const doc = buildContextDocument(loaded);
      setContextDoc(doc);
      setContextReady(true);
      return doc;
    } finally {
      setLoadingContext(false);
    }
  }, [contextDoc, projects, storage]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setLoading(true);

    try {
      const doc = await ensureContext();

      const { supabase } = await import("../lib/supabase");
      if (!supabase) throw new Error("Not connected — please sign in.");

      const { data, error } = await supabase.functions.invoke("project-assistant", {
        body: {
          question: q,
          contextDocument: doc,
          conversationHistory: convHistory,
        },
      });

      if (error) {
        let detail = error.message;
        try { const b = await (error as any).context?.json?.(); detail = b?.error || b?.message || detail; } catch {}
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.error);

      const answer: string = data?.answer || "No response returned.";
      setMessages(prev => [...prev, { role: "assistant", content: answer }]);
      // Keep rolling conversation history for multi-turn context (max 10 exchanges)
      setConvHistory(prev => [
        ...prev,
        { role: "user", content: q },
        { role: "assistant", content: answer },
      ].slice(-20));
    } catch (e: any) {
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${e.message || "Something went wrong."}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const SUGGESTIONS = [
    "Who are we carrying for hardwood flooring?",
    "What $/SF are we holding for drywall across all projects?",
    "Which project has the highest contingency %?",
    "What's our total exposure in structural concrete?",
    "Which trade partners have submitted bids?",
  ];

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 300,
      width: "min(520px, 100vw)",
      display: "flex", flexDirection: "column",
      background: T.bgPanel,
      borderLeft: `1px solid ${T.borderPrimary}`,
      boxShadow: "-8px 0 32px rgba(0,0,0,.25)",
      fontFamily: FONT,
    }}>

      {/* ── Header ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "14px 18px", borderBottom: `1px solid ${T.borderPrimary}`,
        flexShrink: 0,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: "50%",
          background: `${T.borderAccent}22`, border: `1px solid ${T.borderAccentFaint}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon.Search size={14} color={T.borderAccent}/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary }}>Portfolio Assistant</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>
            {contextReady
              ? `${projects.filter(p => p.isActive !== false).length} project${projects.filter(p => p.isActive !== false).length !== 1 ? "s" : ""} loaded`
              : loadingContext
              ? "Loading your projects…"
              : "Ask a question to load your portfolio"}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "4px 6px", display: "flex", lineHeight: 1 }}>
          <Icon.X size={16}/>
        </button>
      </div>

      {/* ── Messages ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>

        {messages.length === 0 && !loading && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 20 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ marginBottom: 10, opacity: 0.4 }}><Icon.MessageSquare size={24} /></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>Ask anything about your projects</div>
              <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
                I can look up costs, trade partners, quantities, schedule durations,<br/>
                and comparisons across your entire portfolio.
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => { setInput(s); inputRef.current?.focus(); }}
                  style={{
                    background: `${T.borderAccent}0c`, border: `1px solid ${T.borderAccentFaint}`,
                    borderRadius: 6, padding: "8px 12px", textAlign: "left", cursor: "pointer",
                    fontSize: 11, color: T.textTertiary, fontFamily: FONT, lineHeight: 1.4,
                    transition: "background .12s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = `${T.borderAccent}18`)}
                  onMouseLeave={e => (e.currentTarget.style.background = `${T.borderAccent}0c`)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex",
            justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
          }}>
            <div style={{
              maxWidth: "85%",
              padding: "10px 14px",
              borderRadius: msg.role === "user" ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
              background: msg.role === "user"
                ? T.borderAccent
                : msg.error
                ? `${T.negative}18`
                : T.bgApp,
              border: msg.role === "user"
                ? "none"
                : msg.error
                ? `1px solid ${T.negative}44`
                : `1px solid ${T.borderPrimary}`,
              color: msg.role === "user" ? T.textOnGold : msg.error ? T.negative : T.textSecondary,
              fontSize: 12,
              lineHeight: 1.65,
              whiteSpace: "pre-wrap" as any,
              wordBreak: "break-word" as any,
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{
              padding: "10px 14px", borderRadius: "10px 10px 10px 3px",
              background: T.bgApp, border: `1px solid ${T.borderPrimary}`,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {loadingContext && <span style={{ fontSize: 11, color: T.textMuted }}>Loading projects…</span>}
              <span style={{
                display: "inline-flex", gap: 4,
                animation: "oe-pulse 1.4s ease-in-out infinite",
              }}>
                {[0, 1, 2].map(n => (
                  <span key={n} style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: T.borderAccent, opacity: 0.6,
                    animation: `oe-typing-dot 1.2s ${n * 0.2}s ease-in-out infinite`,
                  }}/>
                ))}
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef}/>
      </div>

      {/* ── Input ── */}
      <div style={{
        padding: "12px 18px 16px",
        borderTop: `1px solid ${T.borderPrimary}`,
        flexShrink: 0, background: T.bgPanel,
      }}>
        {convHistory.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
            <button onClick={() => { setMessages([]); setConvHistory([]); }}
              style={{ background: "none", border: "none", fontSize: 10, color: T.textMuted, cursor: "pointer", fontFamily: FONT }}>
              Clear conversation
            </button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about costs, trade partners, quantities, schedule…"
            rows={2}
            style={{
              flex: 1, resize: "none" as any, borderRadius: 10,
              border: `1px solid ${input.trim() ? T.borderAccent + "88" : T.borderSecondary}`,
              background: T.bgApp,
              color: T.textSecondary,
              fontSize: 12, fontFamily: FONT, lineHeight: 1.5,
              padding: "8px 12px",
              outline: "none",
              transition: "border-color .15s",
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            style={{
              flexShrink: 0, width: 36, height: 36,
              borderRadius: 10, border: "none",
              background: input.trim() && !loading ? T.borderAccent : T.borderSecondary,
              color: input.trim() && !loading ? T.textOnGold : T.textMuted,
              cursor: input.trim() && !loading ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background .15s",
            }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1 1l14 7-14 7V9.5l10-1.5L1 6.5V1z"/>
            </svg>
          </button>
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 6, textAlign: "center" }}>
          Enter to send · Shift+Enter for new line
        </div>
      </div>

      <style>{`
        @keyframes oe-typing-dot {
          0%, 100% { transform: translateY(0); opacity: .5; }
          50% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
